import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Alert, Button, Drawer } from '../ui';
import { Sidebar } from './Sidebar.jsx';
import { TopBar } from './TopBar.jsx';
import { MobileNavigation, MoreMenu } from './MobileNavigation.jsx';
import { HistoryPanel } from './HistoryPanel.jsx';
import { titleKeyForPath } from './destinations.js';
import { useDueMissions } from './useDueMissions.js';
import { useAuth } from '../../context/AuthContext';
import { useConversations } from '../../context/ConversationContext';
import { usePlatform } from '../../context/PlatformContext';
import { useSession } from '../../context/SessionContext';
import { useToast } from '../../context/ToastContext';
import { sessionErrorMessage } from '../chat/classifyError.js';
import { useWebSocket } from '../../context/WebSocketContext';
import { canOpenAdmin } from '../../utils/adminAccess.js';

/**
 * The Etch app frame (spec §8).
 *
 * Three deliberate shapes, one component tree: expanded sidebar (≥1200px),
 * icon rail + drawers (640–1199px), top bar + bottom navigation (<640px).
 * Which one shows is decided entirely in `layout.css`, so there is no
 * viewport state in JS to get out of sync with the CSS.
 *
 * Frame-level responsibilities (spec §11/§12):
 * - skip link as the first focusable element, `<main>` focused on route change,
 * - document title `"<Seite> · Etch"`,
 * - offline banner (`role="status"` via `Alert`) and session-expired notice,
 * - the session/WebSocket error toasts previously owned by `Layout`.
 */
export function AppShell() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { status, error, isConnected, isConnecting, isRestoring, createSession } = useSession();
  const { connected: wsConnected, error: wsError } = useWebSocket();
  const { sources } = usePlatform();
  const { user, logout } = useAuth();
  const { newConversation } = useConversations();
  const toast = useToast();
  const dueCount = useDueMissions();

  const [menuOpen, setMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));

  const mainRef = useRef(null);
  const isFirstRoute = useRef(true);
  const prevWsConnected = useRef(wsConnected);
  const prevUser = useRef(user);
  const autoConnectAttempted = useRef(false);

  const isAdmin = canOpenAdmin(user);

  /* -- Route change: focus the main landmark, update the document title ---- */
  useEffect(() => {
    setMenuOpen(false);
    setMoreOpen(false);
    if (isFirstRoute.current) {
      isFirstRoute.current = false;
      return;
    }
    mainRef.current?.focus?.();
  }, [location.pathname]);

  useEffect(() => {
    const key = titleKeyForPath(location.pathname);
    const brand = t('brandKit.name');
    document.title = key ? `${t(key)} · ${brand}` : brand;
  }, [location.pathname, t, i18n.language]);

  /* -- Offline ------------------------------------------------------------- */
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  /* -- Session bootstrapping (moved here from the old `Layout`) ------------
     A user who lands on /missions must get a session too — otherwise the
     WebSocket never connects and the learning views bail on a null workspace. */
  useEffect(() => {
    if (isRestoring || isConnected || isConnecting) return;
    if (autoConnectAttempted.current) return;

    const repos = sources?.repos?.enabled ? sources.repos.repositories || [] : [];
    const hasAxiom = !!sources?.axiom?.enabled;

    if (repos.length > 0) {
      autoConnectAttempted.current = true;
      createSession(repos[0].path).catch((err) => console.warn('Auto-session failed:', err.message));
    } else if (hasAxiom) {
      autoConnectAttempted.current = true;
      createSession(null, { mode: 'axiom' }).catch((err) => console.warn('Axiom auto-session failed:', err.message));
    }
  }, [isRestoring, isConnected, isConnecting, sources, createSession]);

  useEffect(() => {
    if (error) toast.error(sessionErrorMessage(t, error));
  }, [error, t]);

  useEffect(() => {
    if (wsError) toast.error(wsError);
  }, [wsError]);

  useEffect(() => {
    if (prevWsConnected.current && !wsConnected && status === 'connected') {
      toast.warning(t('setup.connectionLost'));
    }
    prevWsConnected.current = wsConnected;
  }, [wsConnected, status]);

  /* -- Session expired ----------------------------------------------------- */
  useEffect(() => {
    if (prevUser.current && !user) toast.warning(t('auth.sessionExpired'));
    prevUser.current = user;
  }, [user]);

  const handleLogout = () => {
    setMoreOpen(false);
    logout();
    navigate('/login', { replace: true });
  };

  const startNewChat = () => {
    newConversation();
    setMenuOpen(false);
    navigate('/chat');
  };

  return (
    <div className="etch-app">
      <a className="etch-skip-link" href="#main-content">
        {t('nav.skipToContent')}
      </a>

      <Sidebar dueCount={dueCount} onOpenHistory={() => setMenuOpen(true)} />
      <TopBar menuOpen={menuOpen} onOpenMenu={() => setMenuOpen(true)} />

      <main id="main-content" className="etch-app__main" tabIndex={-1} ref={mainRef}>
        {!online && (
          <div className="etch-shell-banner">
            <Alert tone="warning">{t('states.offline')}</Alert>
          </div>
        )}
        <Outlet />
      </main>

      <MobileNavigation dueCount={dueCount} moreOpen={moreOpen} onOpenMore={() => setMoreOpen(true)} />

      <Drawer
        open={menuOpen}
        side="start"
        title={t('nav.history')}
        closeLabel={t('a11y.closeDialog')}
        onClose={() => setMenuOpen(false)}
      >
        <div className="etch-shell-drawer-body">
          <Button block onClick={startNewChat}>
            <Plus size={16} aria-hidden="true" />
            {t('nav.newChat')}
          </Button>
          <HistoryPanel onNavigate={() => setMenuOpen(false)} />
        </div>
      </Drawer>

      <Drawer
        open={moreOpen}
        side="bottom"
        title={t('nav.mobileMore')}
        closeLabel={t('a11y.closeDialog')}
        onClose={() => setMoreOpen(false)}
      >
        <MoreMenu isAdmin={isAdmin} onNavigate={() => setMoreOpen(false)} onLogout={handleLogout} />
      </Drawer>
    </div>
  );
}

export default AppShell;
